import { useState, useEffect, useRef } from 'react'
import { getStoredApiKey } from '@/lib/storage/local-storage'
import { validateApiKey } from '@/lib/api/keys'

interface UseUserTierOptions {
  /** Callback to open the settings/share modal after sign-up completes */
  onSettingsOpen: () => void
}

/**
 * Manages user tier state and the post-sign-up flow.
 * - Fetches tier from API on mount and when API key changes
 * - After sign-up, re-opens the settings modal if a share was pending
 */
export function useUserTier({ onSettingsOpen }: UseUserTierOptions) {
  const [userTier, setUserTier] = useState<'free' | 'starter' | 'pro'>('free')
  const pendingShareAfterSignUp = useRef(false)

  // Fetch user tier on mount and when API key changes
  useEffect(() => {
    const fetchTier = async () => {
      const apiKey = getStoredApiKey()
      if (apiKey) {
        try {
          const stats = await validateApiKey(apiKey)
          if (stats.valid && stats.tier) {
            setUserTier(stats.tier)
          }
        } catch (error) {
          console.error('Failed to validate API key:', error)
        }
      }
    }

    fetchTier()

    const handleApiKeyChange = () => fetchTier()
    window.addEventListener('apikey-changed', handleApiKeyChange)
    return () => window.removeEventListener('apikey-changed', handleApiKeyChange)
  }, [])

  // After sign-up completes, re-open the share settings modal
  useEffect(() => {
    const handlePostSignUp = () => {
      if (pendingShareAfterSignUp.current) {
        pendingShareAfterSignUp.current = false
        // Small delay to let the sign-up modal close first
        setTimeout(() => onSettingsOpen(), 150)
      }
    }
    window.addEventListener('apikey-changed', handlePostSignUp)
    return () => window.removeEventListener('apikey-changed', handlePostSignUp)
  }, [onSettingsOpen])

  return { userTier, pendingShareAfterSignUp }
}
